import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getArtistTopTracks, findArtistByName } from '../api/deezerClient';
import { getArtistInfo } from '../api/lastfmClient';
import { trackLinkKey } from '../api/itunesClient';
import { useAppleMusicSongLinks } from '../hooks/useAppleMusicSongLinks';
import { useAudioPreview } from '../hooks/useAudioPreview';
import { buildMusicSearchUrl, DEFAULT_MUSIC_SERVICE, MUSIC_SERVICE_LABELS } from '../utils/exportUtils';
import { useAuth } from '../state/AuthContext';
import ArtistActionPills from './ArtistActionPills';
import MusicServiceIcon from './MusicServiceIcon';
import '../styles/spotlight.css';

// Persist enriched image/Deezer lookups across mounts
const imageCache = new Map();

function SpotlightCard({ recommendation, clusterColor, clusterLabel, onNext, hasNext }) {
  const { user } = useAuth();
  const musicService = user?.musicService || DEFAULT_MUSIC_SERVICE;
  const [expanded, setExpanded] = useState(false);
  const [allTracks, setAllTracks] = useState(null);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [listeners, setListeners] = useState(null);
  const [enrichedImage, setEnrichedImage] = useState(null);
  const [enrichedDeezerId, setEnrichedDeezerId] = useState(null);
  const [justArrived, setJustArrived] = useState(true);
  const cardRef = useRef(null);
  const audio = useAudioPreview();

  // Canonical Apple Music links, filled in as background lookups resolve
  const appleMusicLinks = useAppleMusicSongLinks(allTracks, musicService);

  const rec = recommendation;

  // On mount, suppress hover styling on the action pills until the user's
  // pointer actually moves. Prevents the "stuck hover" on mobile where the
  // finger is still resting on the old pill when the next artist loads.
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const clear = () => setJustArrived(false);
    el.addEventListener('pointermove', clear, { once: true });
    el.addEventListener('pointerleave', clear, { once: true });
    el.addEventListener('pointerdown', clear, { once: true });
    const fallback = setTimeout(clear, 1500);
    return () => {
      el.removeEventListener('pointermove', clear);
      el.removeEventListener('pointerleave', clear);
      el.removeEventListener('pointerdown', clear);
      clearTimeout(fallback);
    };
  }, []);
  const nameKey = useMemo(
    () => (rec?.name ? rec.name.toLowerCase().trim() : ''),
    [rec?.name]
  );

  // Enrich image + Deezer ID when the node is missing them
  useEffect(() => {
    if (!rec) return;
    setEnrichedDeezerId(null);

    const hasImage = rec.imageLarge || rec.image;
    const hasNumericId = rec.id && /^\d+$/.test(String(rec.id));
    if (hasImage && hasNumericId) {
      setEnrichedImage(null);
      return;
    }

    const cached = imageCache.get(nameKey);
    if (cached) {
      setEnrichedImage(cached.image);
      setEnrichedDeezerId(cached.deezerId);
      return;
    }

    let cancelled = false;
    findArtistByName(rec.name, { priority: true })
      .then((deezerArtist) => {
        if (cancelled) return;
        if (deezerArtist) {
          const img = deezerArtist.imageLarge || deezerArtist.image || null;
          imageCache.set(nameKey, { image: img, deezerId: deezerArtist.id });
          setEnrichedImage(img);
          setEnrichedDeezerId(deezerArtist.id);
        } else {
          setEnrichedImage(null);
        }
      })
      .catch(() => { if (!cancelled) setEnrichedImage(null); });

    return () => { cancelled = true; };
  }, [nameKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listener count from Last.fm
  useEffect(() => {
    if (!nameKey) return;
    let cancelled = false;
    getArtistInfo(rec.name)
      .then((info) => { if (!cancelled && info) setListeners(info.listeners); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [nameKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveDeezerId = rec?.id && /^\d+$/.test(String(rec.id))
    ? rec.id
    : enrichedDeezerId;

  // Lazy-load top tracks when expanded
  useEffect(() => {
    if (!expanded || allTracks !== null || loadingTracks) return;
    if (!effectiveDeezerId) return;

    let cancelled = false;
    setLoadingTracks(true);

    getArtistTopTracks(effectiveDeezerId, 10, { priority: true })
      .then((tracks) => { if (!cancelled) setAllTracks(tracks.slice(0, 6)); })
      .catch(() => { if (!cancelled) setAllTracks([]); })
      .finally(() => { if (!cancelled) setLoadingTracks(false); });

    return () => { cancelled = true; };
  }, [expanded, effectiveDeezerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleExpand = useCallback(() => {
    setExpanded((v) => !v);
  }, []);

  if (!rec) return null;

  const imageSrc = rec.imageLarge || rec.image || enrichedImage;
  const relatedSeeds = rec.relatedSeedNames || rec.suggestedBy || [];
  const suggestedLabel = relatedSeeds.length > 0
    ? `Similar to ${relatedSeeds.slice(0, 2).join(' & ')}`
    : null;

  const clusterDotColor = clusterColor
    ? `hsl(${clusterColor.h}, ${clusterColor.s}%, ${clusterColor.l}%)`
    : null;

  return (
    <div
      ref={cardRef}
      className={`spotlight-card${expanded ? ' expanded' : ''}${justArrived ? ' just-arrived' : ''}`}
    >
      <button
        className="spotlight-header"
        onClick={handleToggleExpand}
        aria-expanded={expanded}
      >
        <div className="spotlight-image-wrap">
          {imageSrc ? (
            <img className="spotlight-image" src={imageSrc} alt={rec.name} />
          ) : (
            <div className="spotlight-image spotlight-image-placeholder">
              {rec.name.charAt(0)}
            </div>
          )}
        </div>

        <div className="spotlight-info">
          <div className="spotlight-label-row">
            <span className="spotlight-label">
              <svg viewBox="0 0 24 24" fill="currentColor" width="10" height="10">
                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
              </svg>
              Spotlight
            </span>
            {clusterDotColor && clusterLabel && (
              <span className="spotlight-cluster">
                <span className="spotlight-cluster-dot" style={{ background: clusterDotColor }} />
                {clusterLabel}
              </span>
            )}
          </div>
          <div className="spotlight-name">{rec.name}</div>
          {suggestedLabel && (
            <div className="spotlight-suggested">{suggestedLabel}</div>
          )}
        </div>

        <svg
          className={`spotlight-chevron${expanded ? ' rotated' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          width="16"
          height="16"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div className="spotlight-pills-row">
        <ArtistActionPills
          artistName={rec.name}
          artistId={rec.id || enrichedDeezerId}
          artistImage={rec.image || enrichedImage}
        />
        {onNext && (
          <button
            type="button"
            className="spotlight-next-btn"
            onClick={onNext}
            disabled={!hasNext}
            title={hasNext ? 'Next recommendation' : 'No more recommendations'}
          >
            <span>Next</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        )}
      </div>

      <div className="spotlight-body">
        {rec.genres && rec.genres.length > 0 && (
          <div className="spotlight-genres">
            {rec.genres.slice(0, 5).map((genre) => (
              <span key={genre} className="genre-tag">{genre}</span>
            ))}
          </div>
        )}

        {listeners > 0 && (
          <div className="spotlight-listeners">
            {listeners.toLocaleString()} listeners
          </div>
        )}

        {loadingTracks && (
          <div className="spotlight-tracks-loading">Loading songs...</div>
        )}

        {allTracks && allTracks.length > 0 && (
          <div className="spotlight-tracks">
            {allTracks.map((track) => {
              const isTrackPlaying = audio.isPlaying && audio.currentTrack?.id === track.id;
              return (
                <div key={track.id} className="spotlight-track-row">
                  {track.albumImage ? (
                    <img className="spotlight-track-art" src={track.albumImage} alt="" loading="lazy" />
                  ) : (
                    <div className="spotlight-track-art spotlight-track-art-placeholder" />
                  )}
                  <div className="spotlight-track-info">
                    <span className="spotlight-track-name">{track.name}</span>
                    <span className="spotlight-track-album">{track.albumName}</span>
                  </div>
                  <div className="spotlight-track-actions">
                    {track.previewUrl && (
                      <button
                        className={`spotlight-track-play${isTrackPlaying ? ' playing' : ''}`}
                        onClick={() => {
                          if (isTrackPlaying) audio.pause();
                          else audio.play(track);
                        }}
                        title={isTrackPlaying ? 'Pause' : 'Play preview'}
                      >
                        {audio.currentTrack?.id === track.id && audio.progress > 0 && (
                          <svg className="preview-progress-ring preview-progress-ring--sm" viewBox="0 0 40 40">
                            <circle cx="20" cy="20" r="18" fill="none" stroke="rgba(129, 140, 248, 0.25)" strokeWidth="2" />
                            <circle
                              cx="20" cy="20" r="18"
                              fill="none"
                              stroke="#818cf8"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeDasharray={2 * Math.PI * 18}
                              strokeDashoffset={2 * Math.PI * 18 * (1 - (audio.progress || 0))}
                            />
                          </svg>
                        )}
                        {isTrackPlaying ? (
                          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                            <rect x="6" y="4" width="4" height="16" />
                            <rect x="14" y="4" width="4" height="16" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                            <polygon points="6,3 20,12 6,21" />
                          </svg>
                        )}
                      </button>
                    )}
                    <a
                      className={`spotlight-track-spotify${musicService === 'apple-music' ? ' apple-music' : ''}`}
                      href={appleMusicLinks.get(trackLinkKey(track)) || buildMusicSearchUrl(track, musicService)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Search in ${MUSIC_SERVICE_LABELS[musicService]}`}
                    >
                      <MusicServiceIcon service={musicService} size={16} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {expanded && allTracks && allTracks.length === 0 && !loadingTracks && (
          <div className="spotlight-tracks-none">No songs found</div>
        )}
      </div>
    </div>
  );
}

export default SpotlightCard;
